import axios from "../utils/axios";

/**
 * List all services with pagination and advanced filtering
 * @param {Object} params - Query parameters (page, per_page, search, trashed)
 * @returns {Promise} Services list
 */
export const getServices = async (params) => {
    try {
        const { data } = await axios.get('/services', { params });
        return data;
    } catch (error) {
        console.error("🚀 ~ getServices ~ error:", error);
        throw error;
    }
};

/**
 * Get a single service by ID
 * @param {number|string} id - Service ID
 * @returns {Promise} Service data
 */
export const getServiceById = async (id) => {
    try {
        const { data } = await axios.get(`/services/${id}`);
        return data;
    } catch (error) {
        console.error("🚀 ~ getServiceById ~ error:", error);
        throw error;
    }
};

/**
 * Create a new service
 * @param {Object} serviceData - Service data (title_ar/en, description_ar/en, price, is_active, images)
 * @returns {Promise} Created service
 */
export const createService = async (serviceData) => {
    try {
        const { data } = await axios.post('/services', serviceData);
        return data;
    } catch (error) {
        console.error("🚀 ~ createService ~ error:", error);
        throw error;
    }
};

/**
 * Update an existing service
 * @param {number|string} id - Service ID
 * @param {Object} serviceData - Updated service data
 * @returns {Promise} Updated service
 */
export const updateService = async (id, serviceData) => {
    try {
        const { data } = await axios.put(`/services/${id}`, serviceData);
        return data;
    } catch (error) {
        console.error("🚀 ~ updateService ~ error:", error);
        throw error;
    }
};

/**
 * Soft delete a service
 * @param {number|string} id - Service ID
 * @returns {Promise} Result of deletion
 */
export const deleteService = async (id) => {
    try {
        const { data } = await axios.delete(`/services/${id}`);
        return data;
    } catch (error) {
        console.error("🚀 ~ deleteService ~ error:", error);
        throw error;
    }
};

/**
 * Force delete a service (permanent)
 * @param {number|string} id - Service ID
 * @returns {Promise} Result of deletion
 */
export const forceDeleteService = async (id) => {
    try {
        const { data } = await axios.delete(`/services/${id}/force-delete`);
        return data;
    } catch (error) {
        console.error("🚀 ~ forceDeleteService ~ error:", error);
        throw error;
    }
};

/**
 * Restore a soft-deleted service
 * @param {number|string} id - Service ID
 * @returns {Promise} Restored service
 */
export const restoreService = async (id) => {
    try {
        const { data } = await axios.post(`/services/${id}/restore`);
        return data;
    } catch (error) {
        console.error("🚀 ~ restoreService ~ error:", error);
        throw error;
    }
};

/**
 * Bulk soft delete services
 * @param {Array} items - Array of service IDs
 * @returns {Promise} Result
 */
export const massDestroyServices = async (data) => {
    try {
        const response = await axios.delete('/services/mass-destroy', { data });
        return response.data;
    } catch (error) {
        console.error("🚀 ~ massDestroyServices ~ error:", error);
        throw error;
    }
};

/**
 * Bulk force delete services
 * @param {Array} items - Array of service IDs
 * @returns {Promise} Result
 */
export const massForceDeleteServices = async (data) => {
    try {
        const response = await axios.delete('/services/mass-force-delete', { data });
        return response.data;
    } catch (error) {
        console.error("🚀 ~ massForceDeleteServices ~ error:", error);
        throw error;
    }
};

/**
 * Bulk restore services
 * @param {Array} items - Array of service IDs
 * @returns {Promise} Result
 */
export const massRestoreServices = async (data) => {
    try {
        const response = await axios.post('/services/mass-restore', data);
        return response.data;
    } catch (error) {
        console.error("🚀 ~ massRestoreServices ~ error:", error);
        throw error;
    }
};